'use client';

import { useEffect, useState } from 'react';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

type ConsentChoice = 'granted' | 'denied' | null;

function grantConsent() {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      ad_storage: 'granted',
      analytics_storage: 'granted',
    });
  }
}

function denyConsent() {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      ad_storage: 'denied',
      analytics_storage: 'denied',
    });
  }
}

export function ConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const savedChoice = localStorage.getItem('consentChoice') as ConsentChoice;
    if (savedChoice === null) {
      // No prior decision — show the banner
      setVisible(true);
    } else if (savedChoice === 'granted') {
      // Previously granted — re-apply on mount
      grantConsent();
    }
    // If denied, gtag consent stays at 'denied' (set by the inline default script)
  }, []);

  const handleGrant = () => {
    localStorage.setItem('consentChoice', 'granted');
    grantConsent();

    // Load the gtag.js library now that consent is granted
    if (!document.querySelector('script[src*="googletagmanager.com/gtag/js"]')) {
      const gtagScript = document.createElement('script');
      gtagScript.async = true;
      gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=G-VB0C34S7ZR';
      document.head.appendChild(gtagScript);
    }

    setVisible(false);
  };

  const handleDeny = () => {
    localStorage.setItem('consentChoice', 'denied');
    denyConsent();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      id="consent-banner"
      role="dialog"
      aria-label="Cookie Consent"
      aria-live="polite"
      className="fixed bottom-0 left-0 right-0 z-[999] p-4 sm:p-6 flex justify-center animate-in slide-in-from-bottom-4 duration-500"
    >
      <div className="w-full max-w-3xl bg-gray-900/95 backdrop-blur-xl border border-gray-700/80 rounded-2xl shadow-2xl shadow-black/60 flex flex-col sm:flex-row items-start sm:items-center gap-4 p-5 sm:p-6">
        {/* Icon */}
        <div className="flex-shrink-0 w-10 h-10 bg-indigo-600/20 rounded-xl flex items-center justify-center border border-indigo-500/30 text-xl">
          🍪
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white mb-0.5">We value your privacy</p>
          <p className="text-xs text-gray-400 leading-relaxed">
            We use cookies and analytics to improve your experience. You can choose to accept or decline.{' '}
            <a href="/privacy" className="text-indigo-400 hover:text-indigo-300 underline underline-offset-2 transition-colors">
              Privacy Policy
            </a>
          </p>
        </div>

        {/* Buttons */}
        <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
          <button
            id="consent-deny-button"
            onClick={handleDeny}
            className="flex-1 sm:flex-none px-4 py-2 text-xs font-semibold text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-xl transition-all cursor-pointer"
          >
            Decline
          </button>
          <button
            id="consent-grant-button"
            onClick={handleGrant}
            className="flex-1 sm:flex-none px-5 py-2 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-500 rounded-xl shadow-lg shadow-indigo-600/30 transition-all cursor-pointer"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
